/**
 * One family member on the Where screen.
 *
 * This card is the primary view on a phone — place, battery, driving, and how
 * old the fix is, without pinching a map. Staleness is stated in words on every
 * card, not implied by a color.
 */
import { useState } from 'react';
import Avatar from '../../components/Avatar';
import { api, ApiRequestError } from '../../lib/api';
import type { MemberLocation, MemberSummary } from '../../shared/types';
import {
  batteryLabel,
  clockTime,
  coordLabel,
  FRESHNESS_STYLE,
  freshnessOf,
  relativeTime,
} from './format';

interface Props {
  /** Only ever a member whose phone is linked — the page filters the rest out. */
  member: MemberSummary & { avatar_url?: string | null };
  location: MemberLocation | undefined;
  now: number;
}

/** Collapses a run of fixes at the same place into one "stop". */
function stopsFrom(history: MemberLocation[]): MemberLocation[] {
  const stops: MemberLocation[] = [];
  for (const fix of history) {
    const key = fix.place_name ?? coordLabel(fix.lat, fix.lng);
    const prev = stops[stops.length - 1];
    const prevKey = prev ? (prev.place_name ?? coordLabel(prev.lat, prev.lng)) : null;
    if (prevKey === key) continue;
    stops.push(fix);
  }
  return stops.reverse().slice(0, 8);
}

export default function MemberCard({ member, location, now }: Props) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<MemberLocation[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  async function toggleHistory() {
    const next = !open;
    setOpen(next);
    if (!next || history || loadingHistory) return;

    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const rows = await api.get<MemberLocation[]>(`/location/${member.id}/history`);
      setHistory(rows);
    } catch (err) {
      setHistoryError(
        err instanceof ApiRequestError ? err.message : "Couldn't load the last 24 hours."
      );
    } finally {
      setLoadingHistory(false);
    }
  }

  const battery = location ? batteryLabel(location.battery) : null;
  const freshness = location ? freshnessOf(location.recorded_at, now) : null;
  const place = location ? (location.place_name ?? coordLabel(location.lat, location.lng)) : null;
  const stops = history ? stopsFrom(history) : [];

  return (
    <li className="rounded-card border border-border bg-surface p-4">
      <div className="flex items-start gap-3">
        <Avatar name={member.name} color={member.color} url={member.avatar_url} className="h-11 w-11" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-display text-lg font-semibold text-ink">{member.name}</h3>
            {location?.is_driving && (
              <span className="rounded-full bg-dusk-soft px-2 py-0.5 text-xs font-medium text-dusk">
                Driving
              </span>
            )}
          </div>

          {location ? (
            <>
              <p className="mt-0.5 truncate text-ink-soft">{place}</p>

              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`rounded-full px-2 py-1 font-medium ${FRESHNESS_STYLE[freshness!].className}`}
                >
                  {freshness === 'fresh' ? 'Updated' : FRESHNESS_STYLE[freshness!].note + ' —'}{' '}
                  {relativeTime(location.recorded_at, now)}
                </span>
                <span className="text-ink-faint">at {clockTime(location.recorded_at)}</span>
                {battery && (
                  <span
                    className={`rounded-full px-2 py-1 font-medium ${
                      battery.low ? 'bg-clay-soft text-clay' : 'bg-surface-sunk text-ink-soft'
                    }`}
                  >
                    Battery {battery.text}
                    {battery.low ? ' · Low' : ''}
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="mt-0.5 text-ink-soft">No location yet — waiting for the first poll.</p>
          )}
        </div>
      </div>

      {location && (
        <div className="mt-3 border-t border-border pt-2">
          <button
            type="button"
            onClick={toggleHistory}
            aria-expanded={open}
            className="flex min-h-11 w-full items-center justify-between rounded-lg px-1 text-left text-sm font-medium text-ink-soft hover:text-ink focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
          >
            <span>Where {member.name} has been (24h)</span>
            <span aria-hidden="true" className="text-ink-faint">
              {open ? '−' : '+'}
            </span>
          </button>

          {open && (
            <div className="px-1 pb-1">
              {loadingHistory && <p className="py-2 text-sm text-ink-faint">Loading…</p>}

              {historyError && (
                <p className="py-2 text-sm text-clay" role="alert">
                  {historyError}
                </p>
              )}

              {!loadingHistory && !historyError && history && stops.length === 0 && (
                <p className="py-2 text-sm text-ink-faint">
                  No recorded stops in the last 24 hours.
                </p>
              )}

              {!loadingHistory && stops.length > 0 && (
                <ol className="space-y-1.5 py-2">
                  {stops.map((stop) => (
                    <li key={stop.recorded_at} className="flex gap-3 text-sm">
                      <span className="w-16 shrink-0 tabular-nums text-ink-faint">
                        {clockTime(stop.recorded_at)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-ink-soft">
                        {stop.place_name ?? coordLabel(stop.lat, stop.lng)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
